import { toAdminBookingSearch } from './admin-booking-actions.ts'
import { getCataloguePackage, protectionAddOnLabels } from './booking-domain.ts'

export const ADMIN_BOOKING_SELECT = `
  id, public_reference, created_at, status, payment_status, package_key, ceramic_package_id,
  authoritative_starting_price_cents, deposit_amount_cents,
  job_status, final_job_amount_cents, final_job_currency, work_performed_summary, completed_at,
  customer_id, customer_name, customer_email, customer_phone, name, email, phone,
  vehicle_type, vehicle_make, vehicle_model, vehicle_year, vehicle_colour, registration,
  preferred_date, preferred_time, preferred_time_window, confirmed_date, confirmed_time,
  start_at, end_at, appointment_status, appointment_timezone, appointment_hold_expires_at,
  requires_manual_confirmation, customer_notes, assigned_to, internal_notes,
  stripe_checkout_session_id, stripe_payment_intent_id,
  booking_add_ons(add_on_key, add_on_name),
  booking_payments(status, provider_checkout_session_id, provider_payment_intent_id, amount_cents, paid_at, livemode),
  xero_sync_records(sync_status, settlement_status, settlement_source, invoice_id, invoice_number, invoice_total_cents, deposit_applied_cents, amount_paid_cents, outstanding_cents, online_invoice_url, last_error)
`.replace(/\s+/g, ' ').trim()

export type AdminListStatus =
  | 'Reserved / Awaiting Payment'
  | 'Paid / Confirmed'
  | 'Cancelled'
  | 'Expired'
  | 'Checkout failed'

export type AttentionKind =
  | 'reserved_awaiting_payment'
  | 'checkout_failed'
  | 'new_enquiry'
  | 'confirmed_not_invoiced'

export interface AdminBookingAddOn {
  add_on_key: string
  add_on_name: string | null
}

export interface AdminBookingPayment {
  status: string
  provider_checkout_session_id: string | null
  provider_payment_intent_id: string | null
  amount_cents: number
  paid_at: string | null
  livemode?: boolean | null
}

export interface AdminXeroSync {
  sync_status: string
  settlement_status?: string | null
  settlement_source?: string | null
  invoice_id?: string | null
  invoice_number: string | null
  invoice_total_cents: number | null
  deposit_applied_cents: number | null
  amount_paid_cents?: number | null
  outstanding_cents: number | null
  online_invoice_url?: string | null
  last_error: string | null
}

export interface AdminCanonicalBooking {
  id: string
  public_reference: string
  created_at: string
  status: string
  payment_status: string
  package_key: string | null
  ceramic_package_id: string | null
  authoritative_starting_price_cents: number | null
  deposit_amount_cents: number | null
  job_status?: string | null
  final_job_amount_cents?: number | null
  final_job_currency?: string | null
  work_performed_summary?: string | null
  completed_at?: string | null
  customer_id: string | null
  customer_name: string | null
  customer_email: string | null
  customer_phone: string | null
  name?: string | null
  email?: string | null
  phone?: string | null
  vehicle_type: string | null
  vehicle_make: string | null
  vehicle_model: string | null
  vehicle_year: string | null
  vehicle_colour: string | null
  registration: string | null
  preferred_date: string | null
  preferred_time: string | null
  preferred_time_window: string | null
  confirmed_date: string | null
  confirmed_time: string | null
  start_at: string | null
  end_at: string | null
  appointment_status: string | null
  appointment_timezone: string | null
  appointment_hold_expires_at: string | null
  requires_manual_confirmation: boolean
  customer_notes: string | null
  assigned_to: string | null
  internal_notes: string | null
  stripe_checkout_session_id: string | null
  stripe_payment_intent_id: string | null
  booking_add_ons?: AdminBookingAddOn[]
  booking_payments?: AdminBookingPayment[]
  xero_sync_records?: AdminXeroSync[]
}

export interface AdminQuoteRow {
  id: string
  public_reference: string
  created_at: string
  status: string
  customer_name: string
  customer_email: string
}

export interface AdminCustomerRow {
  id: string
  created_at: string
  full_name: string
  first_name: string | null
  last_name: string | null
  email: string | null
  phone: string | null
  source: string | null
  last_booking_at: string | null
  total_bookings: number | null
}

const OPEN_ENQUIRY_STATUSES = new Set(['new', 'reviewing'])
const CLOSED_ENQUIRY_STATUSES = new Set(['closed'])

export type EnquiryWorkspaceStatus = 'new' | 'replied' | 'closed'
export type AdminBookingFilter = 'all' | 'upcoming' | 'confirmed' | 'awaiting_payment' | 'cancelled' | 'test'
const PAID_PAYMENT_STATUSES = new Set(['paid', 'partially_refunded'])
const FAILED_PAYMENT_STATUSES = new Set(['failed', 'payment_failed'])
const CANCELLED_STATUSES = new Set(['cancelled'])
const XERO_INVOICED = new Set(['synced', 'pending', 'ready'])

export function bookingCustomerName(booking: AdminCanonicalBooking) {
  return booking.customer_name || booking.name || 'Customer TBC'
}

export function bookingCustomerEmail(booking: AdminCanonicalBooking) {
  return (booking.customer_email || booking.email || '').toLowerCase() || null
}

export function bookingCustomerPhone(booking: AdminCanonicalBooking) {
  return booking.customer_phone || booking.phone || null
}

export function bookingPackage(booking: AdminCanonicalBooking) {
  return getCataloguePackage(booking.package_key ?? booking.ceramic_package_id ?? undefined)
}

export function bookingVehicleLabel(booking: AdminCanonicalBooking) {
  return [
    booking.vehicle_year,
    booking.vehicle_colour,
    booking.vehicle_make,
    booking.vehicle_model,
    booking.vehicle_type ? `(${booking.vehicle_type})` : '',
  ].filter(Boolean).join(' ') || 'Vehicle TBC'
}

export function bookingAddOnSummary(addOns?: AdminBookingAddOn[]) {
  return (addOns ?? [])
    .map((item) => item.add_on_name ?? protectionAddOnLabels[item.add_on_key] ?? item.add_on_key)
    .join(', ') || 'None'
}

export function paidDepositCents(booking: AdminCanonicalBooking) {
  return (booking.booking_payments ?? [])
    .filter((payment) => PAID_PAYMENT_STATUSES.has(payment.status))
    .reduce((total, payment) => total + (payment.amount_cents || 0), 0)
}

export function isTestBooking(booking: AdminCanonicalBooking) {
  return !hasLiveStripePayment(booking)
}

export function hasLiveStripePayment(booking: AdminCanonicalBooking) {
  return (booking.booking_payments ?? []).some((payment) => payment.livemode === true)
}

export function canHardDeleteBooking(booking: AdminCanonicalBooking) {
  return isTestBooking(booking)
}

export function enquiryWorkspaceStatus(status: string): EnquiryWorkspaceStatus {
  if (CLOSED_ENQUIRY_STATUSES.has(status)) return 'closed'
  if (OPEN_ENQUIRY_STATUSES.has(status) || status === 'new') return 'new'
  return 'replied'
}

export function adminBookingHref(reference: string, action?: 'view' | 'edit' | 'cancel' | 'delete') {
  const search = toAdminBookingSearch(reference, action)
  const params = new URLSearchParams({ booking: search.booking })
  if (search.action) params.set('action', search.action)
  return `/admin/bookings?${params.toString()}`
}

export function adminEnquiryHref(reference: string) {
  return `/admin/quotes?enquiry=${encodeURIComponent(reference)}`
}

export function matchesBookingQuery(booking: AdminCanonicalBooking, query: string) {
  const needle = query.trim().toLowerCase()
  if (!needle) return true
  const haystack = [
    booking.public_reference,
    bookingCustomerName(booking),
    bookingCustomerEmail(booking),
    bookingCustomerPhone(booking),
    bookingVehicleLabel(booking),
  ].join(' ').toLowerCase()
  return haystack.includes(needle)
}

export function matchesBookingFilter(
  booking: AdminCanonicalBooking,
  filter: AdminBookingFilter,
  now = new Date(),
) {
  const listStatus = bookingListStatus(booking, now)
  switch (filter) {
    case 'upcoming':
      return upcomingConfirmedAppointments([booking], now).length > 0
    case 'confirmed':
      return listStatus === 'Paid / Confirmed'
    case 'awaiting_payment':
      return listStatus === 'Reserved / Awaiting Payment'
    case 'cancelled':
      return listStatus === 'Cancelled'
    case 'test':
      return isTestBooking(booking)
    default:
      return true
  }
}

export function latestXero(booking: AdminCanonicalBooking) {
  return booking.xero_sync_records?.[0] ?? null
}

export function xeroStatusLabel(booking: AdminCanonicalBooking) {
  const record = latestXero(booking)
  if (!record) return 'not_invoiced'
  return record.invoice_number || record.sync_status || 'not_invoiced'
}

export function reservationExpired(booking: AdminCanonicalBooking, now = new Date()) {
  if (booking.appointment_status !== 'reserved') return false
  if (PAID_PAYMENT_STATUSES.has(booking.payment_status)) return false
  if (!booking.appointment_hold_expires_at) return false
  return new Date(booking.appointment_hold_expires_at).getTime() <= now.getTime()
}

export function bookingListStatus(booking: AdminCanonicalBooking, now = new Date()): AdminListStatus {
  if (CANCELLED_STATUSES.has(booking.status) || booking.appointment_status === 'cancelled') {
    return 'Cancelled'
  }
  if (FAILED_PAYMENT_STATUSES.has(booking.status) || FAILED_PAYMENT_STATUSES.has(booking.payment_status)) {
    return 'Checkout failed'
  }
  if (reservationExpired(booking, now)) return 'Expired'
  if (
    PAID_PAYMENT_STATUSES.has(booking.payment_status)
    && (booking.status === 'confirmed' || booking.appointment_status === 'confirmed')
  ) {
    return 'Paid / Confirmed'
  }
  if (PAID_PAYMENT_STATUSES.has(booking.payment_status)) {
    return 'Paid / Confirmed'
  }
  return 'Reserved / Awaiting Payment'
}

export function formatAppointment(
  startAt: string | null | undefined,
  timezone = 'Australia/Melbourne',
) {
  if (!startAt) return 'Not scheduled'
  return new Intl.DateTimeFormat('en-AU', {
    timeZone: timezone,
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(startAt))
}

export function startOfLocalDay(date = new Date()) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

export function startOfWeek(date = new Date()) {
  const day = date.getDay()
  const mondayOffset = day === 0 ? -6 : 1 - day
  const start = startOfLocalDay(date)
  start.setDate(start.getDate() + mondayOffset)
  return start
}

export function startOfMonth(date = new Date()) {
  return new Date(date.getFullYear(), date.getMonth(), 1)
}

function createdInRange(booking: AdminCanonicalBooking, start: Date, now = new Date()) {
  const created = new Date(booking.created_at)
  return created >= start && created <= now
}

export function upcomingConfirmedAppointments(bookings: AdminCanonicalBooking[], now = new Date()) {
  return bookings
    .filter((booking) => (
      booking.appointment_status === 'confirmed'
      && booking.start_at
      && new Date(booking.start_at).getTime() >= now.getTime()
      && !CANCELLED_STATUSES.has(booking.status)
    ))
    .sort((left, right) => String(left.start_at).localeCompare(String(right.start_at)))
}

export function attentionItems(input: {
  bookings: AdminCanonicalBooking[]
  quotes: AdminQuoteRow[]
  now?: Date
}) {
  const now = input.now ?? new Date()
  const items: Array<{
    id: string
    kind: AttentionKind
    title: string
    detail: string
    href: string
  }> = []

  for (const booking of input.bookings) {
    if (
      booking.appointment_status === 'reserved'
      && !PAID_PAYMENT_STATUSES.has(booking.payment_status)
      && !reservationExpired(booking, now)
    ) {
      items.push({
        id: `reserve-${booking.id}`,
        kind: 'reserved_awaiting_payment',
        title: `${booking.public_reference} reserved`,
        detail: 'Appointment hold awaiting A$100 deposit.',
        href: adminBookingHref(booking.public_reference),
      })
    }
    if (FAILED_PAYMENT_STATUSES.has(booking.status) || FAILED_PAYMENT_STATUSES.has(booking.payment_status)) {
      items.push({
        id: `failed-${booking.id}`,
        kind: 'checkout_failed',
        title: `${booking.public_reference} checkout failed`,
        detail: bookingCustomerName(booking),
        href: adminBookingHref(booking.public_reference),
      })
    }
    const xero = latestXero(booking)
    const invoiced = Boolean(xero?.invoice_number)
      || Boolean(xero && XERO_INVOICED.has(xero.sync_status))
    if (bookingListStatus(booking, now) === 'Paid / Confirmed' && !invoiced) {
      items.push({
        id: `invoice-${booking.id}`,
        kind: 'confirmed_not_invoiced',
        title: `${booking.public_reference} not invoiced`,
        detail: 'Confirmed booking has no Xero invoice yet.',
        href: adminBookingHref(booking.public_reference),
      })
    }
  }

  for (const quote of input.quotes) {
    if (OPEN_ENQUIRY_STATUSES.has(quote.status)) {
      items.push({
        id: `quote-${quote.id}`,
        kind: 'new_enquiry',
        title: `${quote.public_reference} enquiry`,
        detail: quote.customer_name,
        href: adminEnquiryHref(quote.public_reference),
      })
    }
  }

  return items.slice(0, 8)
}

export function buildOverviewMetrics(input: {
  bookings: AdminCanonicalBooking[]
  customers: AdminCustomerRow[]
  quotes: AdminQuoteRow[]
  now?: Date
}) {
  const now = input.now ?? new Date()
  const weekStart = startOfWeek(now)
  const monthStart = startOfMonth(now)
  const confirmed = input.bookings.filter((booking) => bookingListStatus(booking, now) === 'Paid / Confirmed')
  const depositsCollectedCents = input.bookings.reduce((total, booking) => total + paidDepositCents(booking), 0)
  const repeatCustomers = input.customers.filter((customer) => (customer.total_bookings ?? 0) > 1).length
  const openEnquiries = input.quotes.filter((quote) => OPEN_ENQUIRY_STATUSES.has(quote.status)).length

  return [
    {
      label: 'UPCOMING APPOINTMENTS',
      value: String(upcomingConfirmedAppointments(input.bookings, now).length),
      note: 'Confirmed ceramic appointments still ahead',
    },
    {
      label: 'BOOKINGS THIS WEEK',
      value: String(input.bookings.filter((booking) => createdInRange(booking, weekStart, now)).length),
      note: 'Created since Monday, including unpaid holds',
    },
    {
      label: 'BOOKINGS THIS MONTH',
      value: String(input.bookings.filter((booking) => createdInRange(booking, monthStart, now)).length),
      note: 'Created this calendar month',
    },
    {
      label: 'DEPOSITS COLLECTED',
      value: new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(depositsCollectedCents / 100),
      note: 'Verified A$100 Stripe deposits only, not package From prices',
    },
    {
      label: 'CONFIRMED BOOKINGS',
      value: String(confirmed.length),
      note: 'Paid ceramic bookings that are confirmed',
    },
    {
      label: 'CUSTOMERS',
      value: String(input.customers.length),
      note: `${repeatCustomers} repeat customer${repeatCustomers === 1 ? '' : 's'}`,
    },
    {
      label: 'ENQUIRIES AWAITING RESPONSE',
      value: String(openEnquiries),
      note: 'Quote requests, kept separate from bookings',
    },
  ]
}

export function customerIdentityKey(email?: string | null, phone?: string | null) {
  const normalisedEmail = email?.trim().toLowerCase()
  if (normalisedEmail) return `email:${normalisedEmail}`
  const normalisedPhone = phone?.replace(/[^0-9+]/g, '')
  if (normalisedPhone) return `phone:${normalisedPhone}`
  return null
}

export function bookingsForCustomer(customer: AdminCustomerRow, bookings: AdminCanonicalBooking[]) {
  return bookings.filter((booking) => {
    if (booking.customer_id && booking.customer_id === customer.id) return true
    const bookingKey = customerIdentityKey(bookingCustomerEmail(booking), bookingCustomerPhone(booking))
    const customerKey = customerIdentityKey(customer.email, customer.phone)
    return Boolean(bookingKey && customerKey && bookingKey === customerKey)
  })
}

export function nextUpcomingBooking(bookings: AdminCanonicalBooking[], now = new Date()) {
  return upcomingConfirmedAppointments(bookings, now)[0] ?? null
}

export function lastBooking(bookings: AdminCanonicalBooking[]) {
  return [...bookings].sort((left, right) => right.created_at.localeCompare(left.created_at))[0] ?? null
}

export function customerVehicles(bookings: AdminCanonicalBooking[]) {
  return [...new Set(bookings.map(bookingVehicleLabel).filter((label) => label !== 'Vehicle TBC'))]
}

export function customerProtectionUsed(bookings: AdminCanonicalBooking[]) {
  return [...new Set(bookings.map((booking) => bookingPackage(booking).displayName))]
}

export function asAdminBookings(data: unknown): AdminCanonicalBooking[] {
  return Array.isArray(data) ? data as AdminCanonicalBooking[] : []
}

export const LEGACY_SERVICE_AUDIT = {
  publicCeramicSource: 'ceramic_packages (1 / 3 / 5 Year GYEON catalog)',
  unusedByPublicFunnel: [
    'basic-detail',
    'standard-detail',
    'premium-detail',
    'full-detail',
    'ceramic-coating',
    'basic-exterior-wash',
  ],
  stillPossiblyReferencedAsDetailingCopy: [
    'interior-detail',
    'exterior-detail',
    'paint-correction',
  ],
  action: 'Do not delete. Archive or hide later after confirming no historic booking.service_id links are needed.',
}
